import { c as _c } from "react/compiler-runtime";
import React, { useState } from 'react';
import classNames from 'classnames';
import { useClpPurchaseContext, useClpSelectedOption, CLPPurchaseOption } from '../../context/clp-purchase-context.react-component';
import { useI18n } from '@udemy-v2/i18n';
import { TextSkeleton } from '@udemy-v2/react-reveal-components';
import { useCommonData } from '@udemy-v2/common-data';
import styles from './course-sidebar-container.module.scss';
import { CollapsibleBuyBox } from '../buy-box/collapsible-buy-box.react-component';
import { CollapsibleSubscriptionPurchaseContent } from '@/features/course-landing-page/components/subscription-purchase-content/collapsible-subscription-purchase-content.react-component';
import { CollapsibleNonCatalogPPContent } from '@/features/course-landing-page/components/non-catalog-pp-offer';
import { ClpIconSecondaryActions } from '@/features/course-landing-page/components/clp-secondary-actions/clp-icon-secondary-actions.react-component';
import { GrowWithGoogleEnrollmentBox } from '@/features/course-landing-page/components/grow-with-google-enrollment-box';
import { CourseContentPlacement } from '@/features/course-landing-page/course-landing-page.react-component';
import { useFeatures } from '@udemy-v2/react-experimentation';
export const CourseSidebarCollapsibleActionsContainerSkeleton = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className={styles.container}><TextSkeleton className={styles.skeleton} withTitle={true} lineCountPerParagraph={3} paragraphCount={4} /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};

/**
 * CourseSidebarCollapsibleActionsContainer component - this renders for enrolled, free, and non-Personal Plan paid courses
 * @constructor
 */
export const CourseSidebarCollapsibleActionsContainer = t0 => {
  const $ = _c(14);
  const {
    gettext
  } = useI18n();
  const commonData = useCommonData();
  const {
    isLoading,
    isCourseInAvailablePlans,
    isEligibleForNonCatalogPPOffer,
    isCourseIncludedInGrowWithGoogleSubscription
  } = useClpPurchaseContext();
  const {
    selectedPurchaseOption
  } = useClpSelectedOption();
  const isSubscriptionPrimary = selectedPurchaseOption === CLPPurchaseOption.SUBSCRIPTION;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["keep_price_box_position"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    featureVariantAssignments: features
  } = useFeatures(t1);
  const keepPriceBoxPosition = features.get("keep_price_box_position");
  const keepPriceBoxPositionEnabled = keepPriceBoxPosition?.configuration?.keepPosition ?? false;
  const [initialPrimaryWasSubscription, setInitialPrimaryWasSubscription] = useState(null);
  if (!isLoading && selectedPurchaseOption !== CLPPurchaseOption.LOADING && initialPrimaryWasSubscription === null) {
    setInitialPrimaryWasSubscription(isSubscriptionPrimary);
  }
  const primarySlotIsSubscription = keepPriceBoxPositionEnabled && initialPrimaryWasSubscription !== null ? initialPrimaryWasSubscription : isSubscriptionPrimary;
  const showSubCopy = commonData.user?.country === "IN" || commonData.visitor.countryCode === "IN";
  let t2;
  if ($[1] !== isCourseInAvailablePlans || $[2] !== showSubCopy) {
    t2 = isCourseInAvailablePlans && <CollapsibleSubscriptionPurchaseContent isSubCopyEnabled={showSubCopy} />;
    $[1] = isCourseInAvailablePlans;
    $[2] = showSubCopy;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const subscriptionContent = t2;
  const hasMultiplePurchaseOptions = isCourseInAvailablePlans || isEligibleForNonCatalogPPOffer;
  let t3;
  if ($[4] !== hasMultiplePurchaseOptions) {
    t3 = <CollapsibleBuyBox showRadio={hasMultiplePurchaseOptions} />;
    $[4] = hasMultiplePurchaseOptions;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const transactionalContent = t3;
  let primaryContent;
  let secondaryContent;
  if (isEligibleForNonCatalogPPOffer) {
    let t4;
    if ($[6] !== showSubCopy) {
      t4 = <CollapsibleNonCatalogPPContent isSubCopyEnabled={showSubCopy} />;
      $[6] = showSubCopy;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    const nonCatalogSubsContent = t4;
    primaryContent = primarySlotIsSubscription ? nonCatalogSubsContent : transactionalContent;
    secondaryContent = primarySlotIsSubscription ? transactionalContent : nonCatalogSubsContent;
  } else {
    if (isCourseInAvailablePlans) {
      primaryContent = primarySlotIsSubscription ? subscriptionContent : transactionalContent;
      secondaryContent = primarySlotIsSubscription ? transactionalContent : subscriptionContent;
    } else {
      primaryContent = transactionalContent;
      secondaryContent = null;
    }
  }
  if (isLoading) {
    let t4;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <CourseSidebarCollapsibleActionsContainerSkeleton />;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  let t4;
  if ($[9] !== gettext || $[10] !== isCourseIncludedInGrowWithGoogleSubscription || $[11] !== primaryContent || $[12] !== secondaryContent) {
    t4 = <>{isCourseIncludedInGrowWithGoogleSubscription ? <div className={classNames(styles.container)}><GrowWithGoogleEnrollmentBox placement={CourseContentPlacement.SIDEBAR} /></div> : <><span className={classNames(styles["collapsible-title"], "ud-heading-xl")}>{gettext("Purchase options")}</span><div className={styles["collapsible-container"]}><div>{primaryContent}{secondaryContent}</div><div><ClpIconSecondaryActions className={styles["collapsible-secondary-actions"]} /></div></div></>}</>;
    $[9] = gettext;
    $[10] = isCourseIncludedInGrowWithGoogleSubscription;
    $[11] = primaryContent;
    $[12] = secondaryContent;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
};